import React, { useState } from 'react';
import { db, storage } from './firebase'; // auth is not used directly in this snippet but kept if needed
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { MdGroupAdd, MdClose, MdCheck, MdSearch } from 'react-icons/md';

const CreateGroupChat = ({ allUsers, user, onGroupCreated, onClose }) => {
    const [groupName, setGroupName] = useState('');
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [groupProfileFile, setGroupProfileFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // State for managing hover effects
    const [hoveredUser, setHoveredUser] = useState(null);
    const [isBtnHovered, setIsBtnHovered] = useState(false);
    const [isCloseHovered, setIsCloseHovered] = useState(false);

    // Get the current user's display name
    const creatorProfile = allUsers.find(u => u.id === user.uid) || { fullName: 'The Admin' };
    const creatorName = creatorProfile.fullName || creatorProfile.userName || 'The Admin';

    // Toggle user selection
    const toggleMember = (memberId) => {
        setSelectedMembers(prev => 
            prev.includes(memberId) 
                ? prev.filter(id => id !== memberId) 
                : [...prev, memberId]
        );
    };

    // Filter users based on search term (excluding current user)
    const filteredUsers = allUsers
        .filter(u => u.id !== user.uid)
        .filter(u => 
            (u.fullName && u.fullName.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (u.userName && u.userName.toLowerCase().includes(searchTerm.toLowerCase()))
        );

    // Handle File Upload
    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setGroupProfileFile(e.target.files[0]);
        }
    };

    // Create Group Logic
    const handleCreateGroup = async () => {
        if (!groupName.trim() || selectedMembers.length === 0) return;

        setLoading(true);
        try {
            let groupImageUrl = null;

            // Upload Group Image if selected
            if (groupProfileFile) {
                const imageRef = ref(storage, `group_images/${Date.now()}_${groupProfileFile.name}`);
                const uploadResult = await uploadBytes(imageRef, groupProfileFile);
                groupImageUrl = await getDownloadURL(uploadResult.ref);
            }

            // Prepare Chat Data
            const newChatData = {
                groupName: groupName.trim(),
                groupProfileImageUrl: groupImageUrl,
                isGroup: true,
                adminId: user.uid,
                participants: [user.uid, ...selectedMembers],
                createdAt: serverTimestamp(),
                lastMessage: `${creatorName} created the group "${groupName.trim()}"`,
                lastMessageTimestamp: serverTimestamp(),
                lastMessageSenderId: user.uid, // Important for notifications
                readStatus: {
                    [user.uid]: serverTimestamp()
                }
            };

            // Add to Firestore
            const docRef = await addDoc(collection(db, 'chats'), newChatData);

            // Add initial system message
            await addDoc(collection(db, 'chats', docRef.id, 'messages'), {
                content: `${creatorName} created the group "${groupName.trim()}"`,
                senderId: 'system',
                timestamp: serverTimestamp(),
                isSystem: true
            });

            onGroupCreated(docRef.id);
            onClose();

        } catch (error) {
            console.error("Error creating group:", error);
            alert("Failed to create group. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // --- Inline Styles ---
    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent black
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '16px',
            boxSizing: 'border-box',
        },
        modalContainer: {
            backgroundColor: 'white',
            width: '100%',
            maxWidth: '450px',
            borderRadius: '12px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '90vh', // Prevent it from being taller than screen
            overflow: 'hidden',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: 'white',
        },
        headerTitle: {
            fontSize: '1.25rem',
            fontWeight: 'bold',
            color: '#1f2937',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
        },
        closeBtn: {
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            borderRadius: '50%',
            color: isCloseHovered ? '#ef4444' : '#6b7280',
            transition: 'color 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        contentBody: {
            padding: '20px',
            overflowY: 'auto', // Scroll content if needed
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
        },
        inputGroup: {
            display: 'flex',
            flexDirection: 'column',
        },
        label: {
            fontSize: '0.875rem',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '6px',
        },
        textInput: {
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1px solid #d1d5db',
            fontSize: '0.95rem',
            outline: 'none',
            width: '100%',
            boxSizing: 'border-box',
        },
        fileInput: {
            fontSize: '0.875rem',
            color: '#6b7280',
        },
        membersSection: {
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: '250px', // Fixed height for user list
        },
        searchBox: {
            display: 'flex',
            alignItems: 'center',
            padding: '8px 12px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
        },
        searchInput: {
            border: 'none',
            background: 'transparent',
            outline: 'none',
            marginLeft: '8px',
            width: '100%',
            fontSize: '0.875rem',
        },
        userList: {
            flex: 1,
            overflowY: 'auto',
            padding: '0',
            margin: '0',
            listStyle: 'none',
        },
        userItem: (isSelected, isHovering) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '10px 12px',
            cursor: 'pointer',
            backgroundColor: isSelected ? '#eff6ff' : (isHovering ? '#f3f4f6' : 'white'), // Blue-ish if selected
            borderBottom: '1px solid #f3f4f6',
            transition: 'background-color 0.2s',
        }),
        userInfo: {
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden', // Handle long names
        },
        profileImg: {
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            objectFit: 'cover',
            marginRight: '10px',
            backgroundColor: '#e5e7eb',
        },
        userName: {
            fontSize: '0.9rem',
            fontWeight: '500',
            color: '#1f2937',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        checkbox: (isSelected) => ({
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            border: isSelected ? '2px solid #2563eb' : '2px solid #9ca3af',
            backgroundColor: isSelected ? '#2563eb' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            transition: 'all 0.2s',
        }),
        footer: {
            padding: '16px 20px',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb',
        },
        createBtn: {
            width: '100%',
            padding: '12px',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: (!groupName.trim() || selectedMembers.length === 0 || loading) ? '#d1d5db' : (isBtnHovered ? '#1d4ed8' : '#2563eb'),
            color: 'white',
            fontWeight: 'bold',
            fontSize: '1rem',
            cursor: (!groupName.trim() || selectedMembers.length === 0 || loading) ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        }
    };

    return (
        <div style={styles.overlay}>
            <div style={styles.modalContainer}>
                
                {/* Header */}
                <div style={styles.header}>
                    <h2 style={styles.headerTitle}>
                        <MdGroupAdd style={{ marginRight: '8px' }} /> 
                        Create Group
                    </h2>
                    <button 
                        onClick={onClose} 
                        style={styles.closeBtn}
                        onMouseEnter={() => setIsCloseHovered(true)}
                        onMouseLeave={() => setIsCloseHovered(false)}
                    >
                        <MdClose size={24} />
                    </button>
                </div>

                {/* Content */}
                <div style={styles.contentBody}>
                    
                    {/* Group Name Input */}
                    <div style={styles.inputGroup}>
                        <label style={styles.label}>Group Name</label>
                        <input
                            type="text"
                            placeholder="Enter group name (e.g., Study Buddies)"
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            style={styles.textInput}
                        />
                    </div>

                    {/* Group Image Input */}
                    <div style={styles.inputGroup}>
                        <label style={styles.label}>Group Icon (Optional)</label>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            style={styles.fileInput}
                        />
                    </div>

                    {/* Select Members Section */}
                    <div style={styles.inputGroup}>
                        <label style={styles.label}>Select Members ({selectedMembers.length})</label>
                        
                        <div style={styles.membersSection}>
                            {/* Search Bar inside list */}
                            <div style={styles.searchBox}>
                                <MdSearch color="#9ca3af" size={20} />
                                <input 
                                    type="text" 
                                    placeholder="Search people..." 
                                    style={styles.searchInput}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            {/* Scrollable List */}
                            <ul style={styles.userList}>
                                {filteredUsers.length > 0 ? (
                                    filteredUsers.map(userItem => {
                                        const isSelected = selectedMembers.includes(userItem.id);
                                        const isHovering = hoveredUser === userItem.id;

                                        return (
                                            <li
                                                key={userItem.id}
                                                style={styles.userItem(isSelected, isHovering)}
                                                onClick={() => toggleMember(userItem.id)}
                                                onMouseEnter={() => setHoveredUser(userItem.id)}
                                                onMouseLeave={() => setHoveredUser(null)}
                                            >
                                                <div style={styles.userInfo}>
                                                    <img 
                                                        src={userItem.profileImageUrl || "https://placehold.co/40x40"} 
                                                        alt="User" 
                                                        style={styles.profileImg} 
                                                    />
                                                    <span style={styles.userName}>
                                                        {userItem.fullName || userItem.userName}
                                                    </span>
                                                </div>
                                                
                                                <div style={styles.checkbox(isSelected)}>
                                                    {isSelected && <MdCheck size={14} />}
                                                </div>
                                            </li>
                                        );
                                    })
                                ) : (
                                    <li style={{ padding: '16px', textAlign: 'center', color: '#6b7280', fontSize: '0.9rem' }}>
                                        No users found.
                                    </li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Footer / Button */}
                <div style={styles.footer}>
                    <button
                        onClick={handleCreateGroup}
                        disabled={loading || !groupName.trim() || selectedMembers.length === 0}
                        style={styles.createBtn}
                        onMouseEnter={() => setIsBtnHovered(true)}
                        onMouseLeave={() => setIsBtnHovered(false)}
                    >
                        {loading ? 'Creating Group...' : 'Create Group'}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default CreateGroupChat;